import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { 
  Lightbulb, 
  PenTool, 
  GitBranch, 
  CreditCard, 
  Share2,
  ChevronRight,
  MessageSquare,
  Building
} from 'lucide-react';
import Modal from '../components/Modal'; // Import the Modal component
import ContactForm from '../components/ContactForm'; // Import the ContactForm


// Import all article components
import GettingStartedArticle from '../components/help/GettingStartedArticle';
import CreatingCustomizingViewsArticle from '../components/help/CreatingCustomizingViewsArticle';
import BuildingInteractiveFlowsArticle from '../components/help/BuildingInteractiveFlowsArticle';
import MaximizingAccessPassArticle from '../components/help/MaximizingAccessPassArticle';
import ExportingSharingDesignsArticle from '../components/help/ExportingSharingDesignsArticle';
import EnterpriseGuideArticle from '../components/help/EnterpriseGuideArticle';

const HelpCenter = () => {
  
  const [activeArticle, setActiveArticle] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const handleContactSuccess = () => {
    setContactSuccess(true);
    // Automatically close the modal after success
    setTimeout(() => {
      setContactModalOpen(false);
      setContactSuccess(false);
    }, 3000);
  };

  const articles = [
    {
      id: 'getting-started',
      title: 'Getting Started with Prototyping',
      description: 'Learn how to set up your first project, understand the interface, and create your first prototype.',
      icon: Lightbulb,
      gradient: 'from-yellow-50 to-orange-50',
      iconColor: 'text-yellow-500',
      component: GettingStartedArticle
    },
    {
      id: 'creating-views',
      title: 'Creating and Customizing Views',
      description: 'Master the process of describing your UI, answering clarification questions, and refining generated views.',
      icon: PenTool,
      gradient: 'from-blue-50 to-indigo-50',
      iconColor: 'text-blue-500',
      component: CreatingCustomizingViewsArticle
    },
    {
      id: 'building-flows',
      title: 'Building Interactive Flows',
      description: 'Combine multiple views into cohesive user journeys to simulate realistic interactions and test user flows.',
      icon: GitBranch,
      gradient: 'from-green-50 to-teal-50',
      iconColor: 'text-green-500',
      component: BuildingInteractiveFlowsArticle
    },
    {
      id: 'maximizing-access',
      title: 'Maximizing Your Access Pass',
      description: 'Understand payment options and strategies to get the most value from your subscription.',
      icon: CreditCard,
      gradient: 'from-purple-50 to-violet-50',
      iconColor: 'text-purple-500',
      component: MaximizingAccessPassArticle
    },
    {
      id: 'exporting-sharing',
      title: 'Exporting and Sharing Your Designs',
      description: 'Learn to export prototypes, create shareable links, and integrate designs into your development workflow.',
      icon: Share2,
      gradient: 'from-red-50 to-pink-50',
      iconColor: 'text-red-500',
      component: ExportingSharingDesignsArticle
    },
    {
      id: 'enterprise-guide',
      title: 'Enterprise Plan Guide',
      description: 'Set up and manage team access with enterprise accounts, including admin features, member management, and best practices.',
      icon: Building,
      gradient: 'from-blue-50 to-cyan-50',
      iconColor: 'text-blue-500',
      component: EnterpriseGuideArticle
    }
  ];

  // If an article is selected, render that article
  if (activeArticle) {
    const ArticleComponent = activeArticle.component;
    return <ArticleComponent onBack={() => setActiveArticle(null)} />;
  }

  // Otherwise, render the help center main page
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar sourcePage="help" />
      
      <div className="max-w-5xl mx-auto p-6">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-4">Help Center</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Find guides and resources to help you create, customize, and share your interactive prototypes.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {articles.map((article) => (
            <div 
              key={article.id}
              className={`bg-gradient-to-r ${article.gradient} p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
              onClick={() => setActiveArticle(article)}
            >
              <div className="flex items-start">
                <article.icon className={`w-6 h-6 ${article.iconColor} mr-3 flex-shrink-0 mt-1`} />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <h2 className="text-xl font-bold mb-2">{article.title}</h2>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-gray-700">{article.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold mb-4">Need Additional Help?</h2>
          <p className="text-gray-700 mb-4">
            If you can't find the information you need in these guides, our support team is here to help.
          </p>
          <button
            onClick={() => setContactModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Contact Support
          </button>
        </div>
      </div>

      {/* Contact Modal */}
      <Modal 
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        title="Contact Support"
      >
        {contactSuccess ? (
          <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2">Message Sent!</h3>
            <p>Thank you for contacting us. We'll get back to you as soon as possible.</p>
          </div>
        ) : (
          <ContactForm onSuccess={handleContactSuccess} />
        )}
      </Modal>
    </div>
  );
};

export default HelpCenter;